import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api, { API, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { format, parseISO } from "date-fns";
import { Rocket, Loader2, Download, Sparkles, ArrowRight, ArrowLeft, CheckCircle2, Calendar as CalendarIcon, ListChecks, Star, ExternalLink } from "lucide-react";
import { LANGS } from "@/lib/langs";
import { pollJob } from "@/lib/pollJob";
import { CvEditor } from "./tailor/CvEditor";
import { STATUSES } from "./tracker/constants";

const MODES = [
  { value: "conservative", label: "Conservative", desc: "Minimal edits." },
  { value: "balanced", label: "Balanced", desc: "Rewrite to match the job." },
  { value: "aggressive", label: "Aggressive", desc: "Maximise keyword match." },
];

const STEPS = ["Job", "Tailor", "Save & Track"];

function SuccessCard({ result, jobTitle, company, onReset, onTracker }) {
  return (
    <Card className="border-green-200 shadow-sm max-w-2xl" data-testid="apply-success">
      <CardContent className="pt-6 text-center space-y-4">
        <div className="mx-auto h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
          <CheckCircle2 className="h-6 w-6 text-green-700" strokeWidth={1.75} />
        </div>
        <div>
          <p className="font-heading text-xl font-bold">Application ready!</p>
          <p className="text-sm text-muted-foreground mt-1">{jobTitle} · {company} was saved and added to your tracker.</p>
        </div>
        <div className="flex flex-wrap items-center justify-center gap-2">
          <a href={`${API}/tailored-cvs/${result.tailored_cv_id}/download`} target="_blank" rel="noreferrer" data-testid="apply-download-cv">
            <Button variant="outline"><Download className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Download CV (PDF)</Button>
          </a>
          {result.cover_letter_id && (
            <a href={`${API}/cover-letters/${result.cover_letter_id}/download`} target="_blank" rel="noreferrer" data-testid="apply-download-cover">
              <Button variant="outline"><Download className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Cover Letter</Button>
            </a>
          )}
          <Button onClick={onTracker} data-testid="apply-go-tracker"><ListChecks className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Go to Tracker</Button>
          <Button variant="ghost" onClick={onReset} data-testid="apply-restart">Apply to another</Button>
        </div>
      </CardContent>
    </Card>
  );
}

function JobPreview({ url }) {
  const [failed, setFailed] = useState(false);
  const open = () => url && window.open(url, "_blank", "noopener,noreferrer");
  if (!url) {
    return (
      <Card className="border-zinc-200 shadow-sm" data-testid="job-preview-empty">
        <CardContent className="py-16 text-center text-sm text-muted-foreground">
          Add a job URL on the right to preview the application form here.
        </CardContent>
      </Card>
    );
  }
  return (
    <Card className="border-zinc-200 shadow-sm overflow-hidden" data-testid="job-preview">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 py-3">
        <CardTitle className="font-heading text-sm">Application form</CardTitle>
        <Button size="sm" className="bg-[#24468C] hover:bg-[#1d3870]" onClick={open} data-testid="job-preview-open-btn">
          Open in new window <ExternalLink className="h-3.5 w-3.5 ml-1.5" />
        </Button>
      </CardHeader>
      <CardContent className="p-0">
        {failed ? (
          <div className="p-8 text-center" data-testid="job-preview-fallback">
            <p className="text-sm font-medium">This site cannot be shown inside Jobsmith.</p>
            <p className="text-sm text-muted-foreground mt-1">Many job boards block embedding for security. Use the button below to open the application in a new window, then copy your tailored details from the next steps.</p>
            <Button className="mt-4 bg-[#24468C] hover:bg-[#1d3870]" onClick={open} data-testid="job-preview-fallback-btn">
              Open application in new window <ExternalLink className="h-4 w-4 ml-1.5" />
            </Button>
          </div>
        ) : (
          <iframe
            src={url}
            title="Job application"
            className="w-full h-[520px] border-0 bg-white"
            data-testid="job-preview-iframe"
            onError={() => setFailed(true)}
            onLoad={(e) => {
              try {
                const d = e.target.contentDocument;
                if (d && d.body && d.body.innerHTML === "") setFailed(true);
              } catch (_) { /* cross-origin frame = loaded fine */ }
            }}
          />
        )}
      </CardContent>
    </Card>
  );
}

export default function ApplyFlow() {
  const navigate = useNavigate();
  const location = useLocation();
  const rid = () => Math.random().toString(36).slice(2);

  const [step, setStep] = useState(0);
  const [baseChoice, setBaseChoice] = useState("profile");
  const [baseCvText, setBaseCvText] = useState("");
  // Step 1
  const [jobUrl, setJobUrl] = useState("");
  const [jobTitle, setJobTitle] = useState("");
  const [company, setCompany] = useState("");
  const [jobDesc, setJobDesc] = useState("");
  const [fetching, setFetching] = useState(false);
  // Step 2
  const [mode, setMode] = useState("balanced");
  const [language, setLanguage] = useState("en");
  const [genCover, setGenCover] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [content, setContent] = useState(null);
  const [alignment, setAlignment] = useState(null);
  const [coverLetter, setCoverLetter] = useState("");
  // Step 3
  const [status, setStatus] = useState("applied");
  const [dateApplied, setDateApplied] = useState(format(new Date(), "yyyy-MM-dd"));
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get("/settings").then(({ data }) => {
      setMode(data.tailoring_mode || "balanced");
      setGenCover(data.generate_cover_letter !== false);
      setLanguage(data.preferred_language || "en");
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const p = location.state?.prefill;
    if (p) {
      if (p.job_title) setJobTitle(p.job_title);
      if (p.company) setCompany(p.company);
      if (p.job_url) setJobUrl(p.job_url);
      if (p.job_description) setJobDesc(p.job_description);
      window.history.replaceState({}, "", window.location.pathname);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchFromUrl = async () => {
    if (!jobUrl.trim()) { toast.error("Enter a job URL first"); return; }
    setFetching(true);
    try {
      const { data } = await api.post("/jobs/fetch-url", { url: jobUrl.trim() });
      if (data.job_description) setJobDesc(data.job_description);
      if (data.job_title && !jobTitle.trim()) setJobTitle(data.job_title);
      if (data.company && !company.trim()) setCompany(data.company);
      toast.success("Fetched job details from URL");
    } catch (e) {
      toast.error(formatApiError(e, "Could not fetch the job description"));
    } finally {
      setFetching(false);
    }
  };

  const generate = async () => {
    setGenerating(true);
    setAlignment(null);
    try {
      const { data: job } = await api.post("/tailoring/generate", {
        job_description: jobDesc, job_url: jobUrl, job_title: jobTitle,
        company, tailoring_mode: mode, generate_cover_letter: genCover, language,
        base_cv_text: baseChoice === "paste" ? baseCvText : "",
      });
      const data = await pollJob(job.job_id, "/tailoring/jobs");
      const cv = data.tailored_cv || {};
      setContent({
        title: cv.title || "",
        summary: cv.summary || "",
        career_highlights: cv.career_highlights || [],
        skills: cv.skills || [],
        experience: (cv.experience || []).map((e) => ({
          role: e.role || "", company: e.company || "", location: e.location || "",
          dates: e.dates || "", bullets: e.bullets || (e.description ? [e.description] : []), _k: rid(),
        })),
        education: (cv.education || []).map((e) => ({ degree: e.degree || "", institution: e.institution || "", dates: e.dates || "", _k: rid() })),
        professional_development: cv.professional_development || [],
      });
      setAlignment(data.alignment || null);
      setCoverLetter(data.cover_letter_text || "");
      toast.success("Tailored CV generated");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message || "Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const setField = (k, v) => setContent((c) => ({ ...c, [k]: v }));
  const setLines = (k, v) => setContent((c) => ({ ...c, [k]: v.split("\n") }));
  const updateExp = (i, k, v) => setContent((c) => { const experience = [...c.experience]; experience[i] = { ...experience[i], [k]: v }; return { ...c, experience }; });
  const updateEdu = (i, k, v) => setContent((c) => { const education = [...c.education]; education[i] = { ...education[i], [k]: v }; return { ...c, education }; });
  const removeExp = (i) => setContent((c) => ({ ...c, experience: c.experience.filter((_, x) => x !== i) }));
  const removeEdu = (i) => setContent((c) => ({ ...c, education: c.education.filter((_, x) => x !== i) }));
  const addExp = () => setContent((c) => ({ ...c, experience: [...c.experience, { _k: rid(), company: "", role: "", location: "", dates: "", bullets: [] }] }));
  const addEdu = () => setContent((c) => ({ ...c, education: [...c.education, { _k: rid(), institution: "", degree: "", dates: "" }] }));

  const saveAndTrack = async () => {
    setSaving(true);
    try {
      const cleanContent = {
        title: content.title, summary: content.summary,
        career_highlights: (content.career_highlights || []).filter(Boolean),
        skills: content.skills,
        experience: content.experience.map(({ _k, ...rest }) => ({ ...rest, bullets: (rest.bullets || []).filter(Boolean) })),
        education: content.education.map(({ _k, ...rest }) => rest),
        professional_development: (content.professional_development || []).filter(Boolean),
      };
      const { data: saved } = await api.post("/tailored-cvs", {
        job_title: jobTitle, company, tailoring_mode: mode,
        content: cleanContent, cover_letter_text: genCover ? coverLetter : null, alignment,
      });
      const { data: app } = await api.post("/applications", {
        company, job_title: jobTitle, job_url: jobUrl || null, job_description: jobDesc || null,
        status, date_applied: dateApplied || null, notes: notes || null,
        tailored_cv_id: saved.tailored_cv_id, cover_letter_id: saved.cover_letter_id || null,
      });
      setResult({ ...saved, application_id: app.application_id });
      toast.success("Saved and added to your Tracker");
    } catch (e) {
      toast.error(formatApiError(e, "Could not save and track"));
    } finally {
      setSaving(false);
    }
  };

  const reset = () => {
    setStep(0); setJobUrl(""); setJobTitle(""); setCompany(""); setJobDesc("");
    setContent(null); setAlignment(null); setCoverLetter(""); setNotes(""); setStatus("applied");
    setDateApplied(format(new Date(), "yyyy-MM-dd")); setResult(null);
  };

  const canNextFromJob = jobTitle.trim() && company.trim();

  return (
    <div className="space-y-6" data-testid="apply-flow-page">
      <div>
        <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight flex items-center gap-2">
          <Rocket className="h-7 w-7 text-accent" strokeWidth={1.5} /> Quick Apply
        </h1>
        <p className="text-muted-foreground mt-1 text-sm">Fetch a job, tailor your CV, and add it to your tracker — in one flow.</p>
      </div>

      {/* Stepper */}
      <div className="flex items-center gap-2" data-testid="apply-stepper">
        {STEPS.map((s, i) => (
          <div key={s} className="flex items-center gap-2">
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${i === step ? "bg-primary text-primary-foreground" : i < step ? "bg-green-100 text-green-800" : "bg-zinc-100 text-muted-foreground"}`}>
              {i < step ? <CheckCircle2 className="h-4 w-4" strokeWidth={2} /> : <span className="h-5 w-5 rounded-full bg-white/20 flex items-center justify-center text-xs">{i + 1}</span>}
              {s}
            </div>
            {i < STEPS.length - 1 && <div className="h-px w-6 bg-zinc-200" />}
          </div>
        ))}
      </div>

      {/* Step 1: Job */}
      {step === 0 && (
        <div className="grid lg:grid-cols-2 gap-6 items-start" data-testid="apply-step0">
        <JobPreview url={jobUrl} />
        <Card className="border-zinc-200 shadow-sm">
          <CardHeader><CardTitle className="font-heading text-lg">1. The job</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wider">Job URL (optional)</Label>
              <div className="flex gap-2">
                <Input value={jobUrl} onChange={(e) => setJobUrl(e.target.value)} placeholder="https://…" data-testid="apply-joburl-input" />
                <Button type="button" variant="outline" onClick={fetchFromUrl} disabled={fetching} className="shrink-0" data-testid="apply-fetch-button">
                  {fetching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                  <span className="ml-1.5 hidden sm:inline">Fetch</span>
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-wider">Job Title</Label>
                <Input value={jobTitle} onChange={(e) => setJobTitle(e.target.value)} data-testid="apply-jobtitle-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-wider">Company</Label>
                <Input value={company} onChange={(e) => setCompany(e.target.value)} data-testid="apply-company-input" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wider">Job Description</Label>
              <Textarea rows={8} value={jobDesc} onChange={(e) => setJobDesc(e.target.value)} placeholder="Paste or fetch the job description…" data-testid="apply-jobdesc-input" />
            </div>
            <div className="flex justify-end">
              <Button onClick={() => setStep(1)} disabled={!canNextFromJob} data-testid="apply-next-1">Next <ArrowRight className="h-4 w-4 ml-1.5" strokeWidth={1.5} /></Button>
            </div>
          </CardContent>
        </Card>
        </div>
      )}

      {/* Step 2: Tailor */}
      {step === 1 && (
        <div className="space-y-6">
          <Card className="border-zinc-200 shadow-sm max-w-2xl">
            <CardHeader><CardTitle className="font-heading text-lg">2. Tailor your CV</CardTitle></CardHeader>
            <CardContent className="space-y-5">
              <div className="space-y-2" data-testid="base-cv-choice">
                <Label className="text-xs font-semibold uppercase tracking-wider">Which CV should we tailor?</Label>
                <RadioGroup value={baseChoice} onValueChange={setBaseChoice} className="grid sm:grid-cols-2 gap-2">
                  <label className={`rounded-md border p-3 cursor-pointer text-sm ${baseChoice === "profile" ? "border-accent bg-accent/5" : "border-zinc-200"}`}>
                    <div className="flex items-center gap-2"><RadioGroupItem value="profile" data-testid="base-profile" /> <span className="font-medium">Use my profile CV</span></div>
                    <p className="text-xs text-muted-foreground mt-1">Recommended. We tailor the CV built from your profile.</p>
                  </label>
                  <label className={`rounded-md border p-3 cursor-pointer text-sm ${baseChoice === "paste" ? "border-accent bg-accent/5" : "border-zinc-200"}`}>
                    <div className="flex items-center gap-2"><RadioGroupItem value="paste" data-testid="base-paste" /> <span className="font-medium">Use a different CV</span></div>
                    <p className="text-xs text-muted-foreground mt-1">Paste the text of another CV to tailor instead.</p>
                  </label>
                </RadioGroup>
                {baseChoice === "paste" && (
                  <Textarea rows={6} value={baseCvText} onChange={(e) => setBaseCvText(e.target.value)} placeholder="Paste the full text of the CV you want tailored for this role…" data-testid="base-cv-text" />
                )}
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wider">Tailoring Mode</Label>
                <RadioGroup value={mode} onValueChange={setMode} className="grid grid-cols-3 gap-2">
                  {MODES.map((m) => (
                    <label key={m.value} className={`rounded-md border p-3 cursor-pointer text-sm ${mode === m.value ? "border-accent bg-accent/5" : "border-zinc-200"}`}>
                      <div className="flex items-center gap-2"><RadioGroupItem value={m.value} data-testid={`apply-mode-${m.value}`} /> <span className="font-medium">{m.label}</span></div>
                      <p className="text-xs text-muted-foreground mt-1">{m.desc}</p>
                    </label>
                  ))}
                </RadioGroup>
              </div>
              <div className="flex flex-wrap items-end gap-4">
                <div className="space-y-1.5 w-48">
                  <Label className="text-xs font-semibold uppercase tracking-wider">Output Language</Label>
                  <Select value={language} onValueChange={setLanguage}>
                    <SelectTrigger data-testid="apply-language-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{LANGS.map((l) => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <label className="flex items-center gap-2 cursor-pointer h-10">
                  <Checkbox checked={genCover} onCheckedChange={(v) => setGenCover(!!v)} data-testid="apply-cover-checkbox" />
                  <span className="text-sm">Generate a cover letter</span>
                </label>
              </div>
              <Button onClick={generate} disabled={generating} data-testid="apply-generate-button" className="active:scale-[0.98] transition-transform">
                {generating ? <><Loader2 className="h-4 w-4 animate-spin mr-1.5" /> Generating…</> : <><Sparkles className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> {content ? "Regenerate" : "Generate Tailored CV"}</>}
              </Button>
              {generating && <p className="text-xs text-muted-foreground">This can take 15–30 seconds while the AI tailors your CV.</p>}
            </CardContent>
          </Card>

          {content && (
            <div className="space-y-4">
              {alignment && typeof alignment.score === "number" && (
                <Badge className={`inline-flex items-center ${alignment.score >= (alignment.target_low ?? 0) ? "bg-green-100 text-green-800 hover:bg-green-100" : "bg-amber-100 text-amber-800 hover:bg-amber-100"}`} data-testid="apply-alignment-badge">
                  <Star className="h-3.5 w-3.5 mr-1 text-gold fill-gold" strokeWidth={1.5} />
                  {alignment.score}% match · {alignment.mode}
                </Badge>
              )}
              <CvEditor content={content} setField={setField} setLines={setLines} updateExp={updateExp} updateEdu={updateEdu} removeExp={removeExp} removeEdu={removeEdu} addExp={addExp} addEdu={addEdu} />
              {genCover && (
                <Card className="border-zinc-200 shadow-sm max-w-3xl mx-auto">
                  <CardHeader><CardTitle className="font-heading text-base">Cover Letter</CardTitle></CardHeader>
                  <CardContent><Textarea rows={10} value={coverLetter} onChange={(e) => setCoverLetter(e.target.value)} data-testid="apply-cover-text" /></CardContent>
                </Card>
              )}
            </div>
          )}

          <div className="flex justify-between max-w-2xl">
            <Button variant="ghost" onClick={() => setStep(0)}><ArrowLeft className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Back</Button>
            <Button onClick={() => setStep(2)} disabled={!content} data-testid="apply-next-2">Next <ArrowRight className="h-4 w-4 ml-1.5" strokeWidth={1.5} /></Button>
          </div>
        </div>
      )}

      {/* Step 3: Save & Track */}
      {step === 2 && !result && (
        <Card className="border-zinc-200 shadow-sm max-w-2xl">
          <CardHeader><CardTitle className="font-heading text-lg">3. Save & add to tracker</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="rounded-md bg-zinc-50 border border-zinc-200 p-3 text-sm">
              <p className="font-medium">{jobTitle}</p>
              <p className="text-muted-foreground">{company}</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-wider">Status</Label>
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger data-testid="apply-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-wider">Date Applied</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start font-normal" data-testid="apply-date-input">
                      <CalendarIcon className="h-4 w-4 mr-2" strokeWidth={1.5} />
                      {dateApplied ? format(parseISO(dateApplied), "PP") : <span className="text-muted-foreground">Pick a date</span>}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar mode="single" selected={dateApplied ? parseISO(dateApplied) : undefined} onSelect={(d) => setDateApplied(d ? format(d, "yyyy-MM-dd") : "")} initialFocus />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wider">Notes</Label>
              <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="apply-notes-input" />
            </div>
            <div className="flex justify-between">
              <Button variant="ghost" onClick={() => setStep(1)}><ArrowLeft className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Back</Button>
              <Button onClick={saveAndTrack} disabled={saving} data-testid="apply-save-track-button" className="active:scale-[0.98] transition-transform">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><ListChecks className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Save & Add to Tracker</>}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Success */}
      {result && (
        <SuccessCard result={result} jobTitle={jobTitle} company={company} onReset={reset} onTracker={() => navigate("/tracker")} />
      )}
    </div>
  );
}
